import { useEffect, useMemo, useRef, useState } from "react";
import { colors, css, fonts, frames, glyphs } from "../../../../Styles";
import { AicoConnection, CheckBox, CheckStyles, LoadingSpinner, MainContentPaddingTypes, NewButton, NewButtonList, NewInput, NewInputList, NewModal, NewSelect, PageHeader, Setting, UpdateServerModal } from "../../../../Components";
import { useAicoConnection } from "../../../../Components/ToolsComponents/_ToolsComponents/aicoConnectionContext";
import { ButtonTypes, LocaleFarmDate, NewFieldStyles, NewInputListHandles, SelectFieldOption_Values, systemTypeOptions } from "../../../../Definitions";
import { Graph_QL, useSystem, useUser } from "../../../../Services";
import { selectAllSystemLimitations } from "../../../../Services/Agrisys_GqlDataCaller/SystemCalls/systemLimitationsSlice";
import { EXTENDED_COMPANIES, ExtendedCompaniesArgs, ExtendedCompaniesResponse } from "../../../../Services/Agrisys_GqlDataCaller/ServiceToolCalls/GQLCalls/extendedCompanies";
import { SystemLimitations } from "../../../../Services/Agrisys_GqlDataCaller/SystemCalls/GQLCalls/systemLimitations";
import { useAppSelector } from "../../../../Store";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { VersionsResponse, VersionsArgs, VERSIONS } from "../../../../Services/Agrisys_GqlDataCaller/ServiceToolCalls/GQLCalls/versions";
import { LimitOffsetPaginationMeta } from "../../../../Services/Agrisys_GqlDataCaller/sharedTypes";
import { IntegrationAccess, IntegrationType } from "../../../../Services/Agrisys_GqlDataCaller/ServiceToolCalls/GQLCalls/companyIntegrationLimitation";
import { INTEGRATION_TYPES, IntegrationTypesResponse } from "../../../../Services/Agrisys_GqlDataCaller/ServiceToolCalls/GQLCalls/integrationTypes";
import { MAINTAIN_COMPANY_INTEGRATION_LIMITATIONS, MaintainCompanyIntegrationLimitationsArgs, MaintainCompanyIntegrationLimitationsResponse } from "../../../../Services/Agrisys_GqlDataCaller/ServiceToolCalls/GQLCalls/maintainCompanyIntegrationLimitations";
import { COMPANY_INTEGRATIONS, CompanyIntegrationsResponse } from "../../../../Services/Agrisys_GqlDataCaller/ServiceToolCalls/GQLCalls/companyIntegrations";
import { useTranslation } from "react-i18next";
import { CreateThirdPartyIntegrationsResponse, CreateThirdPartyIntegrationsArgs, CREATE_THIRD_PARTY_INTEGRATIONS } from "../../../../Services/Agrisys_GqlDataCaller/ServiceToolCalls/GQLCalls/createIntegrationThirdParty";

// TODO
// import { systemTypeOptions } from "../../../../Navigation/Menus/systemMenu";


export default function Company() {
	// #region | GraphQL
	const farm = useAppSelector(({ activeData }) => activeData.farm) ?? "";
	const connection = useMemo(() => Graph_QL.create({ domain: farm }), [farm]);
	const [domain, setDomainState] = useState<string | null>(null);
	const { userFarms } = useUser();
	// #endregion

	function handleDomainChange(value: SelectFieldOption_Values | undefined) {
		setDomainState(value?.value ?? null);
	}

	// #region | State Limitation
	const { getSystemLimitationsInfo, maintainSystemLimitations } = useSystem(connection)
	const stateLimits = useAppSelector(selectAllSystemLimitations);
	const [localLimits, setLocalLimits] = useState<SystemLimitations[] | undefined>(undefined);
	const limitInputRef = useRef<Record<number, NewInputListHandles>>({});

	useEffect(() => {
		if (domain)
			getSystemLimitationsInfo(domain);
	}, [getSystemLimitationsInfo, domain]);

	// Set default values for input fields
	useEffect(() => {
		if (!localLimits) {
			Object.values(limitInputRef.current).forEach((ref) => { ref.refs.current.map((valueHandle) => valueHandle.setValue(0)) })
			return
		};

		localLimits.forEach((l) => {
			const ref = limitInputRef.current[l.fk_system_types];
			if (!ref) return;

			ref.refs.current[0].setValue(l.systems_limit);
			ref.refs.current[1].setValue(l.stations_limit);
		});
	}, [localLimits]);

	useEffect(() => {
		setLocalLimits(stateLimits);
	}, [stateLimits]);

	// Save limits
	const UpdateStateLimits = (systemId: number) => {
		if (!stateLimits) return;

		const ref = limitInputRef.current[systemId];
		if (!ref) return;

		const config = {
			systems_limit: Number(ref.refs.current[0].value) ?? undefined,
			stations_limit: Number(ref.refs.current[1].value) ?? undefined,
		};

		if (!domain) return;

		maintainSystemLimitations(
			domain,
			systemId,
			config.systems_limit,
			config.stations_limit
		);

		setLocalLimits(prev =>
			prev?.map(item => item.fk_system_types === systemId ? {
				...item, systems_limit: config.systems_limit ?? 0, stations_limit: config.stations_limit ?? 0
			} : item)
		);
	};

	// System Input Fields
	const SystemInputs = systemTypeOptions.map((system, index) => {
		const anyDirty = localLimits?.some(local => {
			const backend = stateLimits?.find(x => x.fk_system_types === local.fk_system_types);
			return backend && (
				local.systems_limit !== backend.systems_limit ||
				local.stations_limit !== backend.stations_limit
			);
		}) ?? false;

		const id = system.id;

		const local = localLimits?.find(x => x.fk_system_types === id);
		const backend = stateLimits?.find(x => x.fk_system_types === id);

		const backendSystems = backend?.systems_limit ?? undefined;
		const backendStations = backend?.stations_limit ?? undefined;

		const hasChanged = local?.systems_limit !== backendSystems || local?.stations_limit !== backendStations;

		const disabled = anyDirty && !hasChanged;

		return (
			<div key={id} style={{ display: "grid", gridTemplateColumns: "200px 280px 1fr", gap: "10px" }}>
				<div style={{ display: "flex", alignItems: "center", paddingTop: index === 0 ? "14px" : undefined, ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkSecondary }}>
					{system.label}
				</div>

				<NewInputList
					key={domain}
					ref={(el) => { if (el) limitInputRef.current[system.id] = el }}
					placeholders={["", ""]}
					isDisabled={[disabled, disabled]}
					headers={index === 0 ? ["systems", "locations"] : undefined}
					valueOverrides={{
						valueTypes: ["number", "number"],
						defaultValues: [0, 0]
					}}
					fieldOverrides={{
						fieldStyle: NewFieldStyles.greyTertiary,
						fieldWidth: "137.5px"
					}}
					onChanges={[
						(systemsValue: string) => {
							setLocalLimits(prev => {
								if (prev === undefined) return prev;
								if (!prev.find((item) => item.fk_system_types === id)) {
									if (domain == null) {
										return prev
									}
									return [...prev, { fk_system_types: id, stations_limit: 0 || 0, domain: domain!, systems_limit: Number(systemsValue) }]
								}
								return prev.map(item => item.fk_system_types === id ? { ...item, systems_limit: Number(systemsValue) || 0 } : item);
							});
						},
						(stationsValue: string) => {
							setLocalLimits(prev => {
								if (prev === undefined) return prev;
								if (!prev.find((item) => item.fk_system_types === id)) {
									if (domain == null) {
										return prev
									}
									return [...prev, { fk_system_types: id, stations_limit: Number(stationsValue) || 0, domain: domain!, systems_limit: 0 }]
								}
								return prev.map(item => item.fk_system_types === id ? { ...item, stations_limit: Number(stationsValue) || 0 } : item);
							});
						}]}
				/>

				{hasChanged && (<div style={{ display: "flex", alignItems: "center", paddingTop: index === 0 ? "14px" : undefined, ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkSecondary }}>
					<NewButtonList
						contents={["", ""]}
						buttonType={ButtonTypes.arrowButton}
						startEnhancers={[glyphs.Check({}), glyphs.Close({})]}
						onClick={() => UpdateStateLimits(id)}
					/>
				</div>)}
			</div>
		);
	})
	// #endregion

	// #region | Update Company Version
	const limit = 50;
	const [isUpdateServerModalOpen, setIsUpdateServerModalOpen] = useState<boolean>(false);

	const [selectedVersion, setSelectedVersion] = useState<SelectFieldOption_Values | undefined>(undefined);
	const [pagination, setPagination] = useState<LimitOffsetPaginationMeta | undefined>(undefined)

	const [fetchCompany, { data: companyFetchData, loading: companyFetchLoading, error: companyFetchError }] = useLazyQuery<ExtendedCompaniesResponse, ExtendedCompaniesArgs>(EXTENDED_COMPANIES, {
		fetchPolicy: "no-cache",
		context: {
			headers: {
				tocloud: true
			}
		}
	});

	const [fetchVersions, { data: versionsFetchData, loading: versionsFetchLoading, error: versionsFetchError }] = useLazyQuery<VersionsResponse, VersionsArgs>(VERSIONS, {
		fetchPolicy: "no-cache",
		context: {
			headers: {
				tocloud: true
			}
		}
	});

	const companyData = companyFetchData?.user.extendedCompanies?.[0];

	const companyId = useMemo(() => {

		setSelectedVersion(undefined);

		if (!domain || !userFarms) {
			return null;
		}
		return userFarms.find(e => e.domain === domain)?.company_id ?? null;

	}, [domain, userFarms]);

	const fetchCompanyByDomain = () => {
		if (!companyId) return;

		fetchCompany({
			variables: { input: { ids: [companyId] } }
		});
	};

	useEffect(fetchCompanyByDomain, [companyId]);

	useEffect(() => {
		fetchVersions({
			variables: {
				pagination: {
					limit: limit,
					offset: 0,
				},
				sort: 'DESC',
			}
		});
	}, []);

	const currentVersion = companyData?.version;

	const availableVersions: SelectFieldOption_Values[] = useMemo(() => {
		const versionFetch = versionsFetchData?.server.versionsPaginated
		if (versionFetch) {
			setPagination(versionFetch.meta);
		}

		let versions = versionFetch?.data;

		if (currentVersion) {
			versions = versions?.filter(e => e.version_id > currentVersion.version_id) ?? [];
		}

		return versions?.map(e => ({ label: e.version, id: e.version_id })) ?? [];
	}, [versionsFetchData, currentVersion]);

	// #endregion

	return (
		<frames.Page_inner>
			<PageHeader
				topHeader='Companies'
				subSelect={{
					type: "element",
					// element: < AicoConnection hideLANControllers/>
					element:
						<NewSelect
							placeholder="Domain"
							fieldOverrides={{
								fieldStyle: NewFieldStyles.greyDarkPrimary,
								fieldWidth: "230px"
							}}
							options={userFarms?.map(farm => ({ label: farm.domain, value: farm.domain })) ?? []}
							isClearable
							isSearchable
							onChange={handleDomainChange}
							valueOverrides={{
								defaultValue: domain ? { label: domain, value: domain, id: domain } : undefined
							}}
						/>
				}}
			/>

			<frames._page_inner_content_scroll>
				{domain ? (<div style={{ display: "flex", gap: "20px", alignItems: "center", justifyContent: "center", flexDirection: "column" }}>
					<Setting
						startEnhancer={glyphs.FarmInfo({ size: "25px" })}
						header={"Number of systems and locations"}
						content={<div style={{ display: "flex", flexDirection: "column", gap: "10px" }}> {SystemInputs} </div>}
					/>

					<Setting
						startEnhancer={glyphs.FarmInfo({ size: "25px" })}
						header={"Company version"}
						content={<>{!currentVersion ? <>Company has no version, please contact an admin to update</> :
							<div style={{ display: "flex", flexDirection: "column", gap: "10px", ...fonts.fontSize3, ...fonts.paragraph }}>
								<NewButtonList
									contents={["Fetch", "Update Version"]}
									overrides={{
										baseStyles: { width: "fit-content", backgroundColor: colors.greyTertiary, height: "30px", padding: "0 10px" },
										contentStyles: { color: colors.greyDarkPrimary, textShadow: "none", ...fonts.fontSize4 },
										hoverStyles: { backgroundColor: colors.greyLightPrimary, color: colors.greyDarkPrimary }
									}}
									onClick={(i) => {
										if (i === 0) { fetchCompanyByDomain() };
										if (i === 1) { setIsUpdateServerModalOpen(true) };
									}}
									disabled={[false, !!versionsFetchError || versionsFetchLoading || !selectedVersion || !(domain && typeof selectedVersion?.id === "number")]}
								/>
								{companyFetchLoading && <LoadingSpinner />}
								{companyFetchError && <p style={{ ...fonts.fontSize3, ...fonts.paragraph, color: colors.systemRed, maxWidth: "500px" }}>Error: failed to fetch current version</p>}
								<div style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary }}> current version: {currentVersion?.version ?? "unknown"} </div>
								<div style={{ display: "flex", flexDirection: "row", gap: "10px", ...fonts.fontSize3, ...fonts.paragraph }}>
									<NewSelect
										options={availableVersions}
										onChange={e => setSelectedVersion(e)}
										valueOverrides={{ defaultValue: selectedVersion }}
										isRequired
										isDisabled={!!versionsFetchError || versionsFetchLoading}
										fieldOverrides={{
											fieldStyle: NewFieldStyles.greyTertiary,
										}}
										isSearchable
									/>
									<NewButton
										content={""}
										buttonType={ButtonTypes.glyphButton}
										startEnhancer={glyphs.Fetch({ fillColor: colors.blackPrimary })}
										onClick={() =>
											fetchVersions({
												variables: {
													pagination: {
														limit: limit,
														offset: 0,
													},
													sort: 'DESC',
												}
											})
										}
										disabled={versionsFetchLoading}
									/>
								</div>
								{versionsFetchLoading && <LoadingSpinner />}
								{versionsFetchError && <p style={{ ...fonts.fontSize3, ...fonts.paragraph, color: colors.systemRed, maxWidth: "500px" }}>Error: failed to versions</p>}
								{!versionsFetchLoading && pagination && pagination.hasNextPage && (
									<NewButton
										content={"Fetch more"}
										onClick={() =>
											fetchVersions({
												variables: {
													pagination: {
														limit: limit,
														offset: pagination.offset + limit,
													},
													sort: 'DESC',
												}
											})
										}
										disabled={versionsFetchLoading}
									/>
								)}
							</div>
						}
						</>}
					/>

					{companyId && <Integrations domain={domain} companyId={companyId} />}

				</div>) : (<p style={{ ...css.emptyPen, color: colors.greyLightTertiary, textAlign: "center" }}>Please select a domain</p>)}
			</frames._page_inner_content_scroll>

			{domain && typeof selectedVersion?.id === "number" && isUpdateServerModalOpen &&
				<UpdateServerModal
					closeModal={() => {
						fetchCompanyByDomain();
						setIsUpdateServerModalOpen(false);
					}}
					domain={domain}
					version={selectedVersion.label}
				/>
			}
		</frames.Page_inner>
	)
}

function Integrations({ domain, companyId }: { domain: string, companyId: number }) {
	const [fetchCompany, { data: companyFetchData, loading: companyFetchLoading, error: companyFetchError }] = useLazyQuery<CompanyIntegrationsResponse, ExtendedCompaniesArgs>(COMPANY_INTEGRATIONS, {
		fetchPolicy: "no-cache",
		context: {
			headers: {
				tocloud: true,
			},
		},
	});

	const { data: integrationTypesData, loading: integrationTypesLoading, error: integrationTypesError } = useQuery<IntegrationTypesResponse, ExtendedCompaniesArgs>(INTEGRATION_TYPES, {
		fetchPolicy: "no-cache",
		context: {
			headers: {
				tocloud: true
			}
		}
	});

	const [maintainLimitations, { data: maintainLimitationsData, loading: maintainLimitationsLoading, error: maintainLimitationsError, reset: maintainLimitationsReset }] =
		useMutation<MaintainCompanyIntegrationLimitationsResponse, MaintainCompanyIntegrationLimitationsArgs>(MAINTAIN_COMPANY_INTEGRATION_LIMITATIONS, {
			fetchPolicy: "no-cache",
			context: {
				headers: {
					tocloud: true
				}
			}
		});

	const [toggled, setToggled] = useState<Set<number>>(new Set());

	const companyData = companyFetchData?.user.extendedCompanies?.[0];
	const integrationTypes = integrationTypesData?.company.integrationTypes ?? [];

	const savedIds = new Set(companyData?.integrationLimitations.map(l => l.integration_type_id) ?? []);

	const integrationLimitations = integrationTypes.map(type => ({
		...type,
		isChecked: toggled.has(type.integration_type_id) ? !savedIds.has(type.integration_type_id) : savedIds.has(type.integration_type_id),
	}));

	const dirtyLimitations = integrationLimitations.filter(current => {
		const existingLimit = companyData?.integrationLimitations.find(cleanLimit => cleanLimit.integration_type_id === current.integration_type_id);
		const wasChecked = !!existingLimit;
		return current.isChecked !== wasChecked;
	});

	useEffect(() => {
		fetchCompany({
			variables: { input: { ids: [companyId] } }
		});
		maintainLimitationsReset();
		setToggled(new Set());
	}, [companyId, fetchCompany, maintainLimitationsReset]);

	async function handleMaintainLimitations() {
		if (dirtyLimitations.length) {
			const creates: number[] = [];
			const deletes: number[] = [];

			for (const limitation of dirtyLimitations) {
				if (limitation.isChecked) {
					creates.push(limitation.integration_type_id);
				} else {
					deletes.push(limitation.integration_type_id);
				}
			}

			const result = await maintainLimitations({ variables: { domain, creates, deletes } });

			if (result.data?.company.maintainCompanyIntegrationLimitations.success) {
				setToggled(new Set());
				maintainLimitationsReset();
				await fetchCompany({ variables: { input: { ids: [companyId] } } });
			}
		}
	}

	const dataLoading = companyFetchLoading || integrationTypesLoading || maintainLimitationsLoading;

	const errorMaintain = maintainLimitationsError || (maintainLimitationsData && !maintainLimitationsData.company.maintainCompanyIntegrationLimitations.success);

	const [typeToCreate, setTypeToCreate] = useState<IntegrationType>();

	return (
		<>
			<Setting
				startEnhancer={glyphs.FarmInfo({ size: "25px" })}
				header={"Third party integration limitations"}
				content={
					<div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
						{companyFetchError || integrationTypesError ? <p style={{ ...fonts.fontSize4, ...fonts.paragraph, color: colors.greyDarkPrimary, marginTop: "20px" }}>Error loading data </p>
							:
							<>
								<div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "10px 50px", width: "fit-content", margin: "0 auto" }}>
									{dataLoading && <LoadingSpinner />}
									{!dataLoading &&
										integrationLimitations.map(integration =>
											<CheckBox
												text={integration.integration_name + " (" + integration.description + ")"}
												isChecked={integration.isChecked}
												onChange={() => {
													setToggled(prev => {
														const next = new Set(prev);
														next.has(integration.integration_type_id) ? next.delete(integration.integration_type_id) : next.add(integration.integration_type_id);
														return next;
													});
												}}
												key={integration.integration_type_id}
												checkStyle={CheckStyles.greyTertiary}
											/>
										)
									}
								</div>
								{!!dirtyLimitations.length &&
									<NewButton
										content={"Save"}
										overrides={{
											baseStyles: { width: "fit-content", backgroundColor: colors.greyTertiary, height: "30px", padding: "0 10px" },
											contentStyles: { color: colors.greyDarkPrimary, textShadow: "none", ...fonts.fontSize4 },
											hoverStyles: { backgroundColor: colors.greyLightPrimary, color: colors.greyDarkPrimary }
										}}
										onClick={handleMaintainLimitations}
										disabled={maintainLimitationsLoading}
									/>
								}
								{errorMaintain && <p style={{ ...fonts.fontSize4, ...fonts.paragraph, color: colors.greyDarkPrimary, marginTop: "20px" }}>ERROR: failed to save limitations</p>}
							</>
						}
					</div>
				}
			/>
			<Setting
				startEnhancer={glyphs.FarmInfo({ size: "25px" })}
				header={"Third party integrations"}
				content={
					<div
						style={{
							display: "flex",
							flexDirection: "column",
							gap: "18px",
							width: "100%",
							maxWidth: "950px",
							margin: "0 auto",
						}}
					>
						{companyFetchError ? (
							<p
								style={{
									...fonts.fontSize4,
									...fonts.paragraph,
									color: colors.systemRed,
								}}
							>
								Error loading integrations
							</p>
						) : (
							<>
								{dataLoading && <LoadingSpinner />}

								{!dataLoading && companyData?.integrationLimitations.map((integrationLimitation) => {
									const existingIntegration = companyData.integrations.find((e) => e.integrationType.integration_type_id === integrationLimitation.integration_type_id);

									const expired = !!existingIntegration?.integration_expiration && new Date(existingIntegration.integration_expiration * 1000) < new Date();

									const status = !existingIntegration ? "Not Created" : expired ? "Expired" : "Active";

									const statusColor = !existingIntegration ? colors.greyDarkSecondary : expired ? colors.systemRed : colors.systemGreen;

									return (
										<div
											key={integrationLimitation.integration_type_id}
											style={{
												display: "flex",
												flexDirection: "column",
												gap: "18px",
												padding: "22px",
												borderRadius: "12px",
												border: `1px solid ${colors.greyTertiary}`,
												background: colors.greyLightPrimary,
												boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
											}}
										>
											{/* Header */}
											<div
												style={{
													display: "flex",
													justifyContent: "space-between",
													alignItems: "flex-start",
													gap: "20px",
												}}
											>
												<div
													style={{
														display: "flex",
														flexDirection: "column",
														gap: "6px",
													}}
												>
													<div
														style={{
															...fonts.paragraph,
															...fonts.fontSize2,
															color: colors.greyDarkPrimary,
															fontWeight: 600,
														}}
													>
														{integrationLimitation.integration_name ?? "-"}
													</div>

													<div
														style={{
															...fonts.paragraph,
															...fonts.fontSize4,
															color: colors.greyDarkSecondary,
															maxWidth: "650px",
														}}
													>
														{integrationLimitation.description ?? "No description"}
													</div>
												</div>

												{!existingIntegration ? (integrationLimitation.access_level === 1 &&
													<NewButton
														content={"Create"}
														overrides={{
															baseStyles: {
																width: "fit-content",
																backgroundColor: colors.greyTertiary,
																height: "34px",
																padding: "0 18px",
															},
															contentStyles: {
																color: colors.greyDarkPrimary,
																textShadow: "none",
																...fonts.fontSize4,
															},
															hoverStyles: {
																backgroundColor: colors.greyLightSecondary,
																color: colors.greyDarkPrimary,
															},
														}}
														onClick={() => setTypeToCreate(integrationLimitation)}
													/>
												) : (
													<div
														style={{
															display: "flex",
															alignItems: "center",
															gap: "8px",
															padding: "6px 12px",
															borderRadius: "20px",
															background: colors.greyTertiary,
														}}
													>
														<div
															style={{
																width: "8px",
																height: "8px",
																borderRadius: "50%",
																background: statusColor,
															}}
														/>

														<div
															style={{
																...fonts.paragraph,
																...fonts.fontSize4,
																color: colors.greyDarkPrimary,
															}}
														>
															{status}
														</div>
													</div>
												)}
											</div>

											{/* Details */}
											<div
												style={{
													display: "grid",
													gridTemplateColumns: "160px 1fr",
													rowGap: "12px",
													columnGap: "24px",
													alignItems: "center",
												}}
											>
												{existingIntegration ? (
													<>
														<div
															style={{
																...fonts.paragraph,
																...fonts.fontSize4,
																color: colors.greyDarkSecondary,
															}}
														>
															Name
														</div>

														<div
															style={{
																...fonts.paragraph,
																...fonts.fontSize4,
																color: colors.greyDarkPrimary,
															}}
														>
															{existingIntegration.description ?? "-"}
														</div>

														<div
															style={{
																...fonts.paragraph,
																...fonts.fontSize4,
																color: colors.greyDarkSecondary,
															}}
														>
															Expiration
														</div>

														<div
															style={{
																...fonts.paragraph,
																...fonts.fontSize4,
																color: colors.greyDarkPrimary,
															}}
														>
															{existingIntegration.integration_expiration ? new LocaleFarmDate(existingIntegration.integration_expiration * 1000).toLocaleFarmString() : "No expiration"}
														</div>

														<div
															style={{
																...fonts.paragraph,
																...fonts.fontSize4,
																color: colors.greyDarkSecondary,
																alignSelf: "start",
															}}
														>
															Token
														</div>

														<div
															style={{
																display: "flex",
																alignItems: "center",
																gap: "10px",
															}}
														>
															<div
																style={{
																	flex: 1,
																	padding: "8px 12px",
																	borderRadius: "6px",
																	background: colors.greyTertiary,
																	fontFamily: "monospace",
																	fontSize: "12px",
																	overflow: "hidden",
																	textOverflow: "ellipsis",
																	whiteSpace: "nowrap",
																}}
															>
																{existingIntegration.integration_jwt}
															</div>

															<NewButton
																content={"Copy"}
																overrides={{
																	baseStyles: { width: "fit-content", backgroundColor: colors.greyTertiary, height: "30px", padding: "0 10px" },
																	contentStyles: { color: colors.greyDarkPrimary, textShadow: "none", ...fonts.fontSize4 },
																	hoverStyles: { backgroundColor: colors.greyLightPrimary, color: colors.greyDarkPrimary }
																}}
																onClick={() => navigator.clipboard.writeText(existingIntegration.integration_jwt)}
															/>
														</div>

														{existingIntegration.integrationType.access_level === 1 && (
															<>
																<div
																	style={{
																		...fonts.paragraph,
																		...fonts.fontSize4,
																		color: colors.greyDarkSecondary,
																		alignSelf: "start",
																	}}
																>
																	Permissions
																</div>

																<div
																	style={{
																		display: "flex",
																		gap: "24px",
																		flexWrap: "wrap",
																	}}
																>
																	{[
																		{ name: "temperature", isEnabled: existingIntegration.integrationAccess?.temp },
																		{ name: "water", isEnabled: existingIntegration.integrationAccess?.water },
																		{ name: "weight", isEnabled: existingIntegration.integrationAccess?.weight }
																	].map(e =>
																		<div
																			style={{
																				display: "flex",
																				alignItems: "center",
																				gap: "8px",
																			}}
																			key={e.name}
																		>
																			{e.isEnabled ?
																				<glyphs.Check fillColor={colors.blackPrimary} />
																				:
																				<glyphs.Close fillColor={colors.blackPrimary} />
																			}
																			<span
																				style={{
																					...fonts.paragraph,
																					...fonts.fontSize4,
																				}}
																			>
																				{e.name}
																			</span>
																		</div>
																	)}
																</div>
															</>
														)}
													</>
												) : (
													<div
														style={{
															gridColumn: "1 / span 2",
															padding: "18px",
															borderRadius: "8px",
															background: colors.greyTertiary,
															...fonts.paragraph,
															...fonts.fontSize4,
															color: colors.greyDarkSecondary,
														}}
													>
														This integration has not been created yet.
													</div>
												)}
											</div>
										</div>
									);
								})}
							</>
						)}
					</div>
				}
			/>
			{typeToCreate &&
				<CreateIntegrationModal
					closeModal={() => {
						setTypeToCreate(undefined);
						fetchCompany({
							variables: { input: { ids: [companyId] } },
						});
					}}
					domain={domain}
					integrationType={typeToCreate}
				/>
			}
		</>
	);
}


interface CreateIntegrationModalProps {
	closeModal: () => void;
	domain: string;
	integrationType: IntegrationType;
}

function CreateIntegrationModal({ closeModal, domain, integrationType }: CreateIntegrationModalProps) {
	const { t } = useTranslation();

	const [integrationState, setIntegrationState] = useState<{ description: string, access: Omit<IntegrationAccess, "integration_access_id"> }>({ description: "", access: {} });

	const [fetchData, { data, loading, error, reset }] = useMutation<CreateThirdPartyIntegrationsResponse, CreateThirdPartyIntegrationsArgs>(CREATE_THIRD_PARTY_INTEGRATIONS, {
		fetchPolicy: "no-cache",
		context: {
			headers: {
				tocloud: true
			}
		}
	});

	async function handleCommit() {
		await fetchData({
			variables: {
				domain,
				inputs: [
					{
						description: integrationState.description,
						integrationAccess: {
							temp: integrationState.access.temp ?? false,
							water: integrationState.access.water ?? false,
							weight: integrationState.access.weight ?? false,
						},
						fk_integration_type: integrationType.integration_type_id,
					}
				]
			}
		});
	}

	function handleCloseModal() {
		setIntegrationState({ description: "", access: {} });
		reset();
		closeModal();
	}

	const isError = error || (data?.company && !data.company.createThirdPartyIntegrations.success);

	return (
		<NewModal
			predefinedButtons={{
				onClose: handleCloseModal
			}}
			header={"Create Integration"}
			overrides={{
				mainContentPadding: MainContentPaddingTypes.secondary,
			}}
			mainContent={
				<div
					style={{
						display: "flex",
						flexDirection: "column",
						gap: "5px",
						padding: "0px 20px"
					}}
				>
					<NewInput
						header="description"
						onChange={(e) => setIntegrationState(prev => ({ ...prev, description: e }))}
					/>
					<div
						style={{
							...fonts.header,
							...fonts.fontSize4,
							color: colors.greyLightTertiary,
							padding: "5px"
						}}
					>
						Permissions
					</div>
					<div
						style={{
							display: "flex",
							flexWrap: "wrap",
							gap: "20px",
							alignItems: "center",
						}}
					>
						<CheckBox
							checkStyle={CheckStyles.blue}
							text="Temperature"
							onChange={(e) => setIntegrationState(prev => ({ ...prev, access: { ...prev.access, temp: e } }))}
						/>
						<CheckBox
							checkStyle={CheckStyles.blue}
							text="Water"
							onChange={(e) => setIntegrationState(prev => ({ ...prev, access: { ...prev.access, water: e } }))}
						/>
						<CheckBox
							checkStyle={CheckStyles.blue}
							text="Weight"
							onChange={(e) => setIntegrationState(prev => ({ ...prev, access: { ...prev.access, weight: e } }))}
						/>
					</div>
				</div>
			}
			footerContent={
				<div style={{ margin: "0px 20px" }}>
					{!(loading || isError || data?.company) &&
						<NewButton
							content={t("buttons.create", { defaultValue: "create" })}
							onClick={handleCommit}
							overrides={{
								baseStyles: {
									backgroundColor: colors.greyDarkPrimary,
									border: "3px solid " + colors.blueDarkPrimary
								},
							}}
						/>
					}
					{loading && <LoadingSpinner />}
					{isError && <p style={{ ...fonts.fontSize2, ...fonts.paragraph, color: colors.systemRed }}>Error: failed to create integraton</p>}
					{data?.company.createThirdPartyIntegrations.success && <p style={{ ...fonts.fontSize2, ...fonts.paragraph, color: colors.systemGreen }}>Successfully created</p>}
				</div>
			}
		/>
	);
}